/**
 * 63.13, the gunship (DECISIONS 270 (2), 271; GameConfig.gunships): the dropship armed. Its flight is a dropship's
 * (core/dropship.ts: called with the payload `gunship`, it flies in, circles the point called in a pylon turn with its side
 * gun inward, and leaves after the loiter), so every system that reads a ship reads it — its hit shape, the air defence, the
 * evasion, the crash. This is its fire: each tick its weapons that are ready and have rounds left pick what they engage among
 * the enemies it sees from its sensor ball within GUNSHIP.reachPeople of its point, and fire — the rounds made bullets the
 * bullets' step flies (their bursts by their class, as the airstrike's rounds, owned by the side's commander seat):
 * - **the chin's 30 mm:** bursts at the nearest person in its turret's cone;
 * - **the side gun** (40, 57 or 76 mm: GameConfig.gunshipGun): out of its left, a vehicle or a mount first, else a person;
 * - **the deck's swarm** (271 (6): many small guided rockets of low yield): a salvo rippled from both launchers over the
 *   targets it sees, when there are two or more or a structure — each rocket guided (`steerGuided`) onto its own;
 * - **the sponsons' heavy rockets** (two a side, the vehicle killers): at a vehicle or a mount, one at a time;
 * - **the pods' unguided rockets:** in pairs at what lies ahead of its nose (its run in).
 * Danger close: nothing with one of its own side within GUNSHIP.friendlyPeople. Pure and deterministic (each round's scatter
 * keyed by the seed, the flight, the weapon and the round); with no gunship flying nothing here runs.
 */
import type { Bullet, DropshipFlight, GameState, GunshipLoad, GunshipWeapon, PlayerState, StructureState, Vec3 } from '../types';
import { GUNSHIP, GUNSHIP_ROUNDS, GUNSHIP_GUIDED, type GunshipRound, type GuidedRound } from '../constants/gunship';
import { PHYS_DT } from '../constants';
import { vecAdd, vecDot, vecLength, vecNormalize, vecScale, vecSub, vecCross } from '../math';
import { dacos, dasin, dcos, dsin } from './dmath';
import { SeededRandom } from './random';
import { gunshipCentre, type DropshipPose } from './dropship';
import { flightNow } from './shipNow';
import { airstrikeOwner } from './airstrike';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { personPointOn, personLineOfSight } from './fineTerrain';
import { gravityAccel } from './physics';
import { structureTeam, drives } from './body';
import { turnToward } from './homing';

/** What a gunship engages: a person or a structure (a vehicle, a mount), where it aims, and whether it is a vehicle or a mount (the heavy rockets' and the side gun's first pick). */
export interface Mark { unit?: number; structure?: number; at: Vec3; hard: boolean; dist: number; /** its side's nearest to it (world; Infinity: none on the planet) */ own: number }
/** A shot a gunship fired this tick (for the look and the sound): its flight, the weapon, where it left and where it was aimed. */
export interface GunshipShot { flight: number; team: number; weapon: GunshipWeapon; from: Vec3; to: Vec3 }

const ticksOf = (seconds: number): number => Math.max(1, Math.round(seconds / PHYS_DT));
const personOf = (state: Pick<GameState, 'config'>): number => PERSON_BODY_HEIGHT * personSetOf(state).k;

/** A point in a ship's frame (people: +x its left, y up from its feet, z its front) in the world. */
export function inFrame(pose: DropshipPose, p: readonly number[], person: number): Vec3 {
  const up = vecNormalize(pose.dir), left = vecCross(up, pose.heading);
  return vecAdd(pose.pos, vecScale(vecAdd(vecAdd(vecScale(left, p[0]), vecScale(up, p[1])), vecScale(pose.heading, p[2])), person));
}

/**
 * What a gunship sees to engage now, nearest its point first: the enemy people on foot and the enemy vehicles and mounts within
 * GUNSHIP.reachPeople of the point and GUNSHIP.slantPeople of its eye, in its sight, none of its side within
 * GUNSHIP.friendlyPeople of them (the guided rockets' wider margin, friendlyGuidedPeople, their pick's: `own`). 69.59 step 3:
 * `at`, another centre and reach (a helicopter's: the ground under it, HELI_GUNS's), and `sight` false to leave the sight to the
 * caller (each of its guns from its own muzzle).
 */
export function marksOf(state: GameState, f: DropshipFlight, eye: Vec3, at?: { centre: Vec3; reachPeople: number; slantPeople: number; sight?: boolean }): Mark[] {
  const out = markCandidates(state, f, eye, at);
  return at?.sight === false ? out : out.filter(m => personLineOfSight(eye, m.at, state));
}

/**
 * DECISIONS 384: marksOf's marks before the sight test, in its order — a sight line a mark, asked only when a pick reaches it
 * (stepGunships' `seen`): the closing filter was 62 % of marksOf, and the picks read the list by `find` but for the guided
 * rockets' danger-close list.
 */
function markCandidates(state: GameState, f: DropshipFlight, eye: Vec3, at?: { centre: Vec3; reachPeople: number; slantPeople: number }): Mark[] {
  const planet = state.planets[f.planetIndex];
  if (!planet) return [];
  const ps = personSetOf(state), person = personOf(state), centre = at ? vecNormalize(at.centre) : gunshipCentre(state, f);
  const arc = (dir: Vec3) => dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(dir), centre)))) * planet.radius;
  const reach = (at?.reachPeople ?? GUNSHIP.reachPeople) * person, slant = (at?.slantPeople ?? GUNSHIP.slantPeople) * person, close = GUNSHIP.friendlyPeople * person;
  const own = state.players.filter(p => p.alive && p.teamIndex === f.team && p.planetIndex === f.planetIndex && !p.boarded);
  const out: Mark[] = [];
  const consider = (at: Vec3, dir: Vec3, m: Omit<Mark, 'at' | 'dist' | 'own'>): void => {
    const d = arc(dir);
    if (d > reach || vecLength(vecSub(at, eye)) > slant) return;
    const near = own.reduce((n, p) => Math.min(n, vecLength(vecSub(personPointOn(planet, p.surfaceDir, ps, 0), at))), Infinity);
    if (near < close) return;
    out.push({ ...m, at, dist: d, own: near });
  };
  for (const s of state.structures) {
    if (s.planetIndex !== f.planetIndex || s.health <= 0 || !(drives(s) || s.type === 'pointDefence')) continue;
    const t = structureTeam(state, s);
    if (t === null || t < 0 || t === f.team) continue;
    consider(personPointOn(planet, s.surfaceDir, ps, person), s.surfaceDir, { structure: s.id, hard: true });
  }
  for (const p of state.players) {
    if (!p.alive || p.teamIndex === f.team || p.planetIndex !== f.planetIndex || p.boarded || p.mount) continue;
    consider(personPointOn(planet, p.surfaceDir, ps, 0.9 * person), p.surfaceDir, { unit: p.id, hard: false });
  }
  out.sort((a, b) => a.dist - b.dist || (a.structure ?? -1) - (b.structure ?? -1) || (a.unit ?? -1) - (b.unit ?? -1));
  return out;
}

/** The bearing (radians off `axis`, across the ship's up) and the depression (radians below level) of `to` from `from`. */
export function bearingOf(pose: DropshipPose, from: Vec3, to: Vec3, axis: Vec3): { off: number; down: number } {
  const up = vecNormalize(pose.dir), d = vecSub(to, from), len = vecLength(d);
  const level = vecSub(d, vecScale(up, vecDot(d, up))), ll = vecLength(level);
  const off = ll < 1e-9 ? 0 : dacos(Math.max(-1, Math.min(1, vecDot(vecScale(level, 1 / ll), axis))));
  return { off, down: len < 1e-9 ? 0 : dasin(Math.max(-1, Math.min(1, -vecDot(d, up) / len))) };
}

/** A round from `from` at `to` (its fall allowed for), scattered by its spread; a bullet the bullets' step flies, bursting by its class. */
export function round(state: GameState, id: number, team: number, from: Vec3, to: Vec3, r: GunshipRound, key: string, fall: number | undefined): Bullet {
  const ps = personSetOf(state), person = personOf(state), speed = r.speedPeople * person;
  const dist = vecLength(vecSub(to, from)), t = dist / speed;
  // (aimed over the point by the drop its flight time gives: half the felt gravity it falls by, times the time²)
  const g = vecLength(gravityAccel(to, state.planets)) * (fall ?? ps.bulletGravityScale) * ps.gravityScale;
  const up = vecNormalize(vecSub(to, state.planets[nearest(state, to)].position));
  let dir = vecNormalize(vecSub(vecAdd(to, vecScale(up, 0.5 * g * t * t)), from));
  const rng = new SeededRandom(key), a = rng.next() * 2 * Math.PI, sp = rng.next() * r.spread;
  const side = vecNormalize(vecCross(dir, up)), lift = vecCross(side, dir);
  dir = vecNormalize(vecAdd(dir, vecAdd(vecScale(side, dcos(a) * sp), vecScale(lift, dsin(a) * sp))));
  return { id, ownerId: airstrikeOwner(team), pos: from, vel: vecScale(dir, speed), age: 0, damage: r.damage, life: t * 1.5 + 0.5,
    ...(fall !== undefined ? { gravity: fall } : {}), blast: { radius: r.blast.radiusPeople * person, damage: r.blast.damage }, explosion: r.explosion };
}
/** The planet nearest a point (its index). */
export function nearest(state: Pick<GameState, 'planets'>, p: Vec3): number {
  let best = 0, bd = Infinity;
  state.planets.forEach((q, i) => { const d = vecLength(vecSub(p, q.position)) - q.radius; if (d < bd) { bd = d; best = i; } });
  return best;
}

/** A guided rocket from `from` along `dir` at mark `m`: popped out at its launch speed, its seeker on `m`. */
function guided(state: GameState, id: number, team: number, from: Vec3, dir: Vec3, m: Mark, r: GuidedRound): Bullet {
  const person = personOf(state);
  return { id, ownerId: airstrikeOwner(team), pos: from, vel: vecScale(vecNormalize(dir), r.launchPeople * person), age: 0, damage: 0, life: r.life, gravity: 0,
    blast: { radius: r.blast.radiusPeople * person, damage: r.blast.damage }, explosion: r.explosion,
    guide: { ...(m.unit !== undefined ? { unit: m.unit } : {}), ...(m.structure !== undefined ? { structure: m.structure } : {}), at: m.at, turn: r.turn, top: r.topPeople * person, accel: r.accelPeople * person, loft: r.loft } };
}

/**
 * One tick of every gunship's fire (loitering, or on its run in): the rounds it fires made bullets, its load's counts and
 * ticks moved on; the shots, for the look. The same state when none fires.
 */
export function stepGunships(state: GameState, tick: number): { state: GameState; shots: GunshipShot[] } {
  const a = state.dropships;
  if (!a?.flights.some(f => f.gunship)) return { state, shots: [] };
  const shots: GunshipShot[] = [], made: Bullet[] = [];
  let next = state.bulletsFired ?? 0;
  const person = personOf(state), seed = String(state.config.seed ?? '');
  const flights = a.flights.map(f => {
    const load = f.gunship;
    if (!load || f.evade?.kind === 'break') return f;   // (DECISIONS 277: broken off for home, it holds its fire)
    const due = (w: GunshipWeapon) => load.left[w] > 0 && tick >= load.next[w];
    if (!(['chin', 'side', 'swarm', 'heavy', 'pods'] as const).some(due)) return f;
    const pose = flightNow(state, f, tick)?.flight?.pose;   // (69.17: the ship as it is now, through shipNow)
    if (!pose || (pose.phase !== 'loiter' && pose.phase !== 'in')) return f;
    // (DECISIONS 384: each mark's sight asked once, when a pick reaches it — the same picks as over marksOf's sighted list)
    const eye = inFrame(pose, GUNSHIP.eye, person), cands = markCandidates(state, f, eye), sight = new Int8Array(cands.length);
    const seen = (q: Mark): boolean => { const i = cands.indexOf(q); if (!sight[i]) sight[i] = personLineOfSight(eye, q.at, state) ? 1 : -1; return sight[i] > 0; };
    const find = (pred: (q: Mark) => boolean): Mark | undefined => cands.find(q => pred(q) && seen(q));
    if (!cands.some(seen)) return f;
    const L: GunshipLoad = { ...load, left: { ...load.left }, next: { ...load.next } };
    const up = vecNormalize(pose.dir), left = vecCross(up, pose.heading);
    const shoot = (w: GunshipWeapon, from: Vec3, b: Bullet, to: Vec3) => { made.push(b); L.left[w]--; shots.push({ flight: f.id, team: f.team, weapon: w, from, to }); };
    const key = (w: GunshipWeapon) => `${seed}-gunship-${f.id}-${w}-${L.left[w]}`;
    // the chin: bursts at the nearest person in its turret's cone
    if (due('chin')) {
      const from = inFrame(pose, GUNSHIP.muzzles.chin, person), C = GUNSHIP.cones.chin;
      const m = find(q => !q.hard && (() => { const b = bearingOf(pose, from, q.at, pose.heading); return b.off <= C.bearing && b.down >= C.depression; })());
      if (m) {
        shoot('chin', from, round(state, next++, f.team, from, m.at, GUNSHIP_ROUNDS.chin[L.chin ?? GUNSHIP.chinGun], key('chin'), undefined), m.at);
        L.burst--;
        L.next.chin = tick + ticksOf(L.burst > 0 ? GUNSHIP_ROUNDS.chin[L.chin ?? GUNSHIP.chinGun].interval : GUNSHIP.chinRest);
        if (L.burst <= 0) L.burst = GUNSHIP.chinBurst;
      }
    }
    // the side gun: out of its left, a vehicle or a mount first
    if (due('side') && pose.phase === 'loiter') {
      const from = inFrame(pose, GUNSHIP.muzzles.side, person), C = GUNSHIP.cones.side, r = GUNSHIP_ROUNDS.side[L.gun];
      const inCone = (q: Mark) => { const b = bearingOf(pose, from, q.at, left); return b.off <= C.bearing && b.down >= C.depression[0] && b.down <= C.depression[1]; };
      const m = find(q => q.hard && inCone(q)) ?? find(inCone);
      if (m) { shoot('side', from, round(state, next++, f.team, from, m.at, r, key('side'), undefined), m.at); L.next.side = tick + ticksOf(r.interval); }
    }
    // the deck's swarm: a salvo rippled over what it sees (two or more, or a structure), a rocket a mark in turn, the launchers by turns
    const danger = (q: Mark) => q.own >= GUNSHIP.friendlyGuidedPeople * person;   // (the guided rockets': danger close wider)
    const clear = due('swarm') ? cands.filter(q => danger(q) && seen(q)) : [];
    if (due('swarm') && clear.length && (L.salvo > 0 || clear.length >= 2 || clear.some(q => q.hard))) {
      if (L.salvo <= 0) L.salvo = Math.min(GUNSHIP.salvo, L.left.swarm);
      const k = L.left.swarm, side = k % 2 === 0 ? 0 : 1, m = clear[k % clear.length];
      const from = inFrame(pose, GUNSHIP.muzzles.swarm[side], person), s = side === 0 ? 1 : -1;
      const dir = vecAdd(vecScale(up, dcos(GUNSHIP.deckLean)), vecScale(left, s * dsin(GUNSHIP.deckLean)));
      shoot('swarm', from, guided(state, next++, f.team, from, dir, m, GUNSHIP_GUIDED.swarm), m.at);
      L.salvo--;
      L.next.swarm = tick + ticksOf(L.salvo > 0 ? GUNSHIP_GUIDED.swarm.interval : GUNSHIP.salvoRest);
    }
    // the sponsons' heavy rockets: at a vehicle or a mount, one at a time, the sides by turns
    if (due('heavy')) {
      const m = find(q => danger(q) && q.hard);
      if (m) {
        const s = L.heavySide, from = inFrame(pose, GUNSHIP.muzzles.heavy[s > 0 ? 0 : 1], person);
        const dir = vecAdd(vecAdd(vecScale(pose.heading, 0.9), vecScale(up, -0.2)), vecScale(left, 0.15 * s));
        shoot('heavy', from, guided(state, next++, f.team, from, dir, m, GUNSHIP_GUIDED.heavy), m.at);
        L.heavySide = s > 0 ? -1 : 1;
        L.next.heavy = tick + ticksOf(GUNSHIP_GUIDED.heavy.interval);
      }
    }
    // the pods: a pair at what lies ahead of its nose (its run in), unguided
    if (due('pods')) {
      const C = GUNSHIP.cones.pods, ahead = inFrame(pose, [0, 1.55, 0], person);
      const m = find(q => { const b = bearingOf(pose, ahead, q.at, pose.heading); return b.off <= C.bearing && b.down >= C.depression[0] && b.down <= C.depression[1]; });
      if (m) {
        for (const i of [0, 1] as const) {
          if (L.left.pods <= 0) break;
          const from = inFrame(pose, GUNSHIP.muzzles.pods[i], person);
          shoot('pods', from, round(state, next++, f.team, from, m.at, GUNSHIP_ROUNDS.pods, key('pods'), 0), m.at);
        }
        L.next.pods = tick + ticksOf(GUNSHIP_ROUNDS.pods.interval);
      }
    }
    return L.left.chin === load.left.chin && L.left.side === load.left.side && L.left.swarm === load.left.swarm && L.left.heavy === load.left.heavy && L.left.pods === load.left.pods ? f : { ...f, gunship: L };
  });
  if (!made.length) return { state, shots };
  return { state: { ...state, dropships: { ...a, flights }, bullets: [...(state.bullets ?? []), ...made], bulletsFired: next }, shots };
}

/** Where a guided rocket aims at a person (its chest) or a structure (a person up), while it stands; else null. */
function guideAt(state: GameState, g: NonNullable<Bullet['guide']>): Vec3 | null {
  const ps = personSetOf(state), person = personOf(state);
  if (g.unit !== undefined) {
    const p: PlayerState | undefined = state.players.find(q => q.id === g.unit);
    return p?.alive ? personPointOn(state.planets[p.planetIndex], p.surfaceDir, ps, 0.9 * person) : null;
  }
  if (g.structure !== undefined) {
    const s: StructureState | undefined = state.structures.find(q => q.id === g.structure);
    return s && s.health > 0 ? personPointOn(state.planets[s.planetIndex], s.surfaceDir, ps, person) : null;
  }
  return null;
}

/**
 * 63.13: the guided rockets steered a tick, before the bullets' step flies them — each at its mark's place now (its last if the
 * mark is gone) lifted by its loft over the ground still to go (so it arcs up and comes down on top), turned toward it at
 * most its turn, its motor pushing it on to its top speed. The same state when none flies.
 */
export function steerGuided(state: GameState, dt: number): GameState {
  const bs = state.bullets;
  if (!bs?.some(b => b.guide)) return state;
  const bullets = bs.map(b => {
    const g = b.guide;
    if (!g) return b;
    const at = guideAt(state, g) ?? g.at;
    const up = vecNormalize(vecSub(at, state.planets[nearest(state, at)].position)), to = vecSub(at, b.pos);
    const ground = vecLength(vecSub(to, vecScale(up, vecDot(to, up))));
    const want = vecNormalize(vecSub(vecAdd(at, vecScale(up, g.loft * ground)), b.pos)), speed = vecLength(b.vel);
    const dir = turnToward(vecScale(b.vel, 1 / speed), want, g.turn * dt);
    return { ...b, vel: vecScale(dir, Math.min(g.top, speed + g.accel * dt)), ...(at !== g.at ? { guide: { ...g, at } } : {}) };
  });
  return { ...state, bullets };
}
